import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LogIn, LogOut, User, UserPlus, Settings, CreditCard, ChevronRight, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ProjectSwitcher from './projectScope/ProjectSwitcher';
import ProjectScopeSync from '../scope/ProjectScopeSync';
import { isProjectScopedPanel } from '../scope/projectScopedPanels';
import ExtensionTitleBarActions from '../extensions/ExtensionTitleBarActions';
import { useExtensionStore } from '../extensions/store';
import BoardsTitleBarSearch from './BoardsTitleBarSearch';
import NotificationIcon from './ui/NotificationIcon';
import SyncStatusIndicator from './ui/SyncStatusIndicator';
import OfflineIndicator from './ui/OfflineIndicator';
import EnvironmentPicker from './EnvironmentPicker';
import MockServerControl from './MockServerControl';
import { useAuthStore } from '../store/authStore';
import { setActiveTab, useUiModalStore } from '../store/uiModalStore';
import { useTeamsStore } from '../store/teamsStore';
import { isTauri } from '../utils/tauri';
import UserAvatar from './ui/UserAvatar';
import AvatarStatusBadge from './ui/AvatarStatusBadge';
import StatusMenu from './ui/StatusMenu';
import { requestSignOut } from '../store/signOutStore';
import { GatedTitleBar, MacTrafficLights, WindowButtons } from './titlebar/WindowControls';
import ShareTitleBadge from './projects/run/share/ShareTitleBadge';
import PaletteSearchButton from './palette/PaletteSearchButton';
const PLAN_CFG = {
  free:       { bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.25)', color: '#64748b' },
  pro:        { bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.25)',  color: '#93c5fd' },
  team:       { bg: 'rgba(139,92,246,0.12)',  border: 'rgba(139,92,246,0.3)',   color: '#a78bfa' },
  enterprise: { bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)',   color: '#fbbf24' },
};

const TauriTitleBar = ({
  workspaces,
  currentWorkspace,
  onWorkspaceChange,
  onEditWorkspace,
  onDeleteWorkspace,
  onForkWorkspace,
  forkingWorkspaceId,
  onPublishLocalWorkspace,
  publishingWorkspaceId,
  onPublishToNetwork,
  canPublishToNetwork,
  onOpenWorkspaceModal,
  onShareWorkspace,
  showToast,
  environments,
  selectedEnvironment,
  onSelectedEnvironmentChange,
  onOpenEnvironmentsModal,
  onOpenAuthModal,
  onOpenSettings,
  updateAvailable,
  onOpenUpdate,
  onTeamJoined,
  onOpenGitWorkspace,
  onExportWorkspace,
  collections,
  requestTabs,
  schemaTabs,
  schemas,
  // The desktop sign-in gate is up (renderApp: desktopGate !== 'app'). Only
  // the drag strip and the window's own buttons remain; see GatedTitleBar.
  gated = false,
}) => {
  const { t } = useTranslation();
  const [tauriWindow, setTauriWindow] = useState(null);
  const [isMac, setIsMac] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /Mac/.test(ua) && !/iPhone|iPad|iPod/.test(ua);
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuTriggerRef = useRef(null);
  /** Fixed positioning keeps the panel inside the viewport (root uses overflow-hidden + rounded corners). */
  const [userMenuPosition, setUserMenuPosition] = useState(null);

  const { user, isAuthenticated, logout } = useAuthStore();
  const teamsCount = useTeamsStore((s) => s.teams.length);
  const activeTab = useUiModalStore((s) => s.activeTab);
  // Workspace / environment / mock controls belong to the API-client context
  // (Collections & History). Extension panels (DB, Boards, Documents, Chat) and
  // Teams/Admin have no use for them, so they're hidden outside that context.
  // Off while the API Client is switched off (Settings → Extensions); the gate
  // keeps those pages from opening, this keeps their controls out too.
  const apiClientEnabled = useExtensionStore((s) => s.apiClientEnabled);
  const showApiContextControls = apiClientEnabled !== false && (activeTab === 'collections' || activeTab === 'history');

  // Note: initializeAuth is now called from App.js to prevent double initialization

  useEffect(() => {
    // Web app (card 1.5): there is no native window. Calling the Tauri window
    // API in a browser threw on every load and logged "Failed to initialize
    // Tauri window … reading 'metadata'"; the window buttons are desktop-only
    // and stay unrendered there anyway.
    if (!isTauri()) return;
    const setupTauriWindow = async () => {
      try {
        const { getCurrentWindow } = await import('@tauri-apps/api/window');
        const currentWindow = getCurrentWindow();

        setTauriWindow(currentWindow);
        console.log('Tauri window initialized successfully');

        try {
          const { platform } = await import('@tauri-apps/plugin-os');
          setIsMac(platform() === 'macos');
        } catch {
          setIsMac(false);
        }
      } catch (err) {
        console.error('Failed to initialize Tauri window:', err);
        setIsMac(false);
      }
    };

    setupTauriWindow();
  }, []);
  const handleMinimize = async () => {
    try {
      if (!tauriWindow) {
        console.log('Window not initialized yet');
        return;
      }

      await tauriWindow.minimize();
      console.log('Window minimized');
    } catch (err) {
      console.error('Failed to minimize window:', err);
    }
  };

  const handleMaximize = async () => {
    try {
      if (!tauriWindow) {
        console.log('Window not initialized yet');
        return;
      }

      await tauriWindow.toggleMaximize();
      console.log('Window maximized/restored');
    } catch (err) {
      console.error('Failed to toggle maximize:', err);
    }
  };

  const handleClose = async () => {
    try {
      if (!tauriWindow) {
        console.log('Window not initialized yet');
        return;
      }

      await tauriWindow.close();
      console.log('Window closed');
    } catch (err) {
      console.error('Failed to close window:', err);
    }
  };

  const handleLoginClick = (e) => {
    e.stopPropagation();
    if (isAuthenticated) {
      const opening = !showUserMenu;
      setShowUserMenu(opening);
      if (opening) window.dispatchEvent(new CustomEvent('titlebar-dropdown-open', { detail: 'profile' }));
    } else {
      onOpenAuthModal?.('login');
    }
  };

  // Shared by the account-menu status row and the status dot on that menu's
  // avatar, so either can open the same list and neither can disagree about it.
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const handleLogout = async () => {
    // Explicit sign-out: the only kind that deletes this account's local data.
    // requestSignOut asks first when some of it exists only on this device.
    setShowUserMenu(false);
    await requestSignOut();
  };

  // Sign out, then immediately reopen the login flow. The IdP is asked for a
  // fresh login on every interactive sign-in (`prompt=login` in oidcService),
  // so this really does land on the login form rather than completing again as
  // the account that just left.
  //
  // NO purge: switching is not leaving. Someone hopping between a work and a
  // personal account would otherwise lose the outgoing account's MCP token,
  // grants and local-only items every single time — and those exist nowhere
  // else. The data stays in that account's own folder, which the next account
  // cannot see, and signing out properly still clears it.
  const handleSwitchAccount = async () => {
    await logout({ reason: 'switch_account' });
    setShowUserMenu(false);
    onOpenAuthModal?.('login');
  };

  // Close profile menu when another titlebar dropdown opens
  useEffect(() => {
    const handler = (e) => { if (e.detail !== 'profile') setShowUserMenu(false); };
    window.addEventListener('titlebar-dropdown-open', handler);
    return () => window.removeEventListener('titlebar-dropdown-open', handler);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click was outside the user menu area
      const userMenuContainer = event.target.closest('.user-menu-container');
      if (!userMenuContainer && showUserMenu) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showUserMenu]);

  const USER_MENU_WIDTH = 256;

  useLayoutEffect(() => {
    if (!showUserMenu || !isAuthenticated) {
      setUserMenuPosition(null);
      return;
    }
    const el = userMenuTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const padding = 10;
    const gap = 8;
    let left = rect.right - USER_MENU_WIDTH;
    const minLeft = padding;
    const maxLeft = window.innerWidth - USER_MENU_WIDTH - padding;
    left = Math.max(minLeft, Math.min(left, maxLeft));
    setUserMenuPosition({
      position: 'fixed',
      left,
      top: rect.bottom + gap,
      width: USER_MENU_WIDTH,
      zIndex: 10000,
    });
  }, [showUserMenu, isAuthenticated]);

  useEffect(() => {
    if (!showUserMenu) return;
    const close = () => setShowUserMenu(false);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [showUserMenu]);

  const titleBarDragStyle = isMac ? 'no-drag' : 'drag';

  if (gated) {
    // ProjectScopeSync renders nothing; it stays mounted so its "workspaces
    // seen so far" memory is the same whether or not the gate was up first.
    return (
      <>
        <ProjectScopeSync workspaces={workspaces} currentWorkspace={currentWorkspace} onWorkspaceChange={onWorkspaceChange} isAuthenticated={isAuthenticated} />
        <GatedTitleBar
          isMac={isMac}
          inTauri={isTauri()}
          ready={Boolean(tauriWindow)}
          onClose={handleClose}
          onMinimize={handleMinimize}
          onMaximize={handleMaximize}
        />
      </>
    );
  }

  return (
    <>
    <div
      data-testid="titlebar"
      className={`flex items-center justify-between py-1 bg-gray-50 dark:bg-[#111113] app-titlebar select-none ${
        isMac ? '' : 'title-bar-drag-region'
      }`}
      style={{
        WebkitAppRegion: titleBarDragStyle,
        userSelect: 'none',
        height: 32,
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        cursor: isMac ? 'default' : 'move',
      }}
      {...(isMac ? {} : { 'data-tauri-drag-region': 'true' })}
    >
      {/* Left: native traffic-light strip must NOT be a drag target (WebKit would steal hover from AppKit). */}
      <div
        className="flex px-2 items-center space-x-3"
        style={{ WebkitAppRegion: isMac ? 'no-drag' : 'drag' }}
      >
        {isMac && isTauri() && (
          <MacTrafficLights ready={Boolean(tauriWindow)} onClose={handleClose} onMinimize={handleMinimize} onMaximize={handleMaximize} />
        )}
        {/* Project scope (cards 1.2 / 2.1): the sync runs on every panel; the
            switcher shows where the scope applies, before the panel's own
            controls. In API Requests the API workspaces are inside it
            (API Client › Workspaces, 21.10.2026); there is no separate
            workspace dropdown any more. */}
        <ProjectScopeSync workspaces={workspaces} currentWorkspace={currentWorkspace} onWorkspaceChange={onWorkspaceChange} isAuthenticated={isAuthenticated} />
        {isProjectScopedPanel(activeTab) && (
          <div style={{ WebkitAppRegion: 'no-drag' }} className="title-bar-no-drag">
            <ProjectSwitcher
              workspaceControls={
                showApiContextControls
                  ? {
                      workspaces,
                      currentWorkspace,
                      onWorkspaceChange,
                      onEditWorkspace,
                      onDeleteWorkspace,
                      onForkWorkspace,
                      forkingWorkspaceId,
                      onPublishLocalWorkspace,
                      publishingWorkspaceId,
                      isAuthenticated,
                      onOpenWorkspaceModal,
                      onShareWorkspace,
                    }
                  : null
              }
            />
          </div>
        )}
        {!showApiContextControls && (
          <div style={{ WebkitAppRegion: 'no-drag' }} className="title-bar-no-drag">
            <ExtensionTitleBarActions panelId={activeTab} />
          </div>
        )}
      </div>

      {/* Panel search — absolutely centered in the titlebar so it reads as part
          of the chrome, independent of the left/right clusters. The search
          self-hides (returns null) unless its panel wants it shown. Chat's
          search lives in the ChatSidebar header now, not here. */}
      {activeTab === 'task-manager.panel' && (
        <div
          className="title-bar-no-drag"
          style={{
            WebkitAppRegion: 'no-drag',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
          }}
        >
          <BoardsTitleBarSearch />
        </div>
      )}

      {isMac && (
        <div
          className="flex-1 min-w-[48px] self-stretch"
          data-tauri-drag-region="true"
          style={{ WebkitAppRegion: 'drag', minHeight: 32 }}
          aria-hidden
        />
      )}

      {/* Right side: Notifications, Login/User Menu and Window Controls */}
      <div
        className="flex flex-shrink-0 items-center space-x-2 pr-2 tauri-window-controls title-bar-no-drag"
        style={{ WebkitAppRegion: 'no-drag' }}
      >
        {showApiContextControls && (
          <>
            <EnvironmentPicker
              environments={environments}
              selectedEnvironment={selectedEnvironment}
              onSelectedEnvironmentChange={onSelectedEnvironmentChange}
              onOpenEnvironmentsModal={onOpenEnvironmentsModal}
            />
            <MockServerControl
              collections={collections}
              requestTabs={requestTabs}
              schemaTabs={schemaTabs}
              schemas={schemas}
              showToast={showToast}
              currentWorkspace={currentWorkspace}
              isAuthenticated={isAuthenticated}
              user={user}
              onOpenSettings={onOpenSettings}
            />
          </>
        )}
        {/* The update indicator moved to the bottom of the activity rail, where
            it sits at the same size as every other icon and is where people
            look for app-level state. Kept out of here rather than shown in
            both places: two entry points to one dialog is two things to
            notice and one of them to wonder about. */}
        {/* "Search ⌘K": opens the command palette with the mouse (PLAN §2.1). */}
        <PaletteSearchButton />
        <button
          type="button"
          onClick={() => onOpenSettings?.()}
          className="p-1.5 rounded-md bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors cursor-pointer"
          aria-label={t('titlebar.settings')}
          title={t('titlebar.settings')}
          style={{ WebkitAppRegion: 'no-drag' }}
        >
          <Settings size={16} />
        </button>
        {/* Self-hiding (isOffline only goes true for a kept signed-in session,
            so no isAuthenticated gate needed — and gating it would hide the
            one case it exists for if isAuthenticated ever lagged). */}
        {isAuthenticated && <ShareTitleBadge onOpen={() => setActiveTab('projects.panel')} />}
        <OfflineIndicator />
        {isAuthenticated && <SyncStatusIndicator />}

        {/* Teams — moved here from the left icon rail; badge mirrors NotificationIcon */}
        {isAuthenticated && (
          <button
            type="button"
            onClick={() => setActiveTab('teams')}
            className={`relative px-1 bg-transparent rounded-lg transition-colors cursor-pointer ${
              activeTab === 'teams'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-800'
            }`}
            style={{ WebkitAppRegion: 'no-drag' }}
            title={teamsCount > 0 ? `${t('sidebar.teams')} (${teamsCount})` : t('sidebar.teams')}
            aria-label={t('sidebar.teams')}
          >
            <Users size={16} />
            {teamsCount > 0 && (
              <span
                className="absolute flex items-center justify-center font-semibold"
                style={{
                  bottom: '-3px',
                  right: '-1px',
                  width: 13,
                  height: 13,
                  borderRadius: 9999,
                  background: 'var(--accent)',
                  color: 'var(--accent-fg, #fff)',
                  fontSize: 8,
                  lineHeight: 1,
                }}
              >
                {teamsCount > 99 ? '99+' : teamsCount}
              </span>
            )}
          </button>
        )}

        {isAuthenticated && (
          <NotificationIcon showToast={showToast} onTeamJoined={onTeamJoined} />
        )}

        {/* Account button — avatar, first name and plan as plain text
            ("Samin · Pro"), the way Claude shows it. */}
        <div className="relative user-menu-container">
          <button
            ref={userMenuTriggerRef}
            type="button"
            onClick={handleLoginClick}
            className={`mr-1 flex items-center py-1 text-xs font-medium bg-transparent text-gray-700 dark:text-zinc-200 dark:hover:bg-zinc-800 transition-colors ${isAuthenticated ? 'gap-1.5 pl-1 pr-2 rounded-md hover:bg-gray-100' : 'space-x-2 px-3 rounded hover:bg-gray-50 hover:text-blue-600 dark:hover:text-blue-400'}`}
            style={{ WebkitAppRegion: 'no-drag' }}
            title={
              isAuthenticated
                ? t('titlebar.signedInAs', { name: user?.name || 'User' })
                : t('titlebar.signInHint')
            }
          >
            {isAuthenticated ? (
              <>
                <UserAvatar userId={user?.id} name={user?.name || 'U'} size={20}>
                  <AvatarStatusBadge userId={user?.id} avatarSize={20} />
                </UserAvatar>
                <span className="titlebar-account-name">
                  {(user?.name || 'User').split(' ')[0]}
                </span>
                <span className="titlebar-account-dot" aria-hidden>{'·'}</span>
                <span className="titlebar-account-plan">{user?.plan ?? 'free'}</span>
              </>
            ) : (
              <>
                <LogIn size={14} />
                <span className="hidden sm:inline">{t('titlebar.signIn')}</span>
              </>
            )}
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && isAuthenticated && userMenuPosition && (
            <div className="acct-menu" style={userMenuPosition}>
              {/* Header */}
              <div className="acct-menu__header">
                <UserAvatar className="acct-menu__avatar" userId={user?.id} name={user?.name || 'U'}>
                  {/* `.acct-menu__avatar` sizes the circle in CSS (32px), so the
                      badge geometry is told that size explicitly.

                      The badge is a BUTTON here, not decoration: the owner asked
                      for the dot on this avatar to be what opens the status
                      list. It drives the same `StatusMenu` below rather than
                      rendering a second copy, so there is one list and one
                      source of truth for whether it is open. */}
                  <button
                    type="button"
                    className="acct-menu__status-trigger"
                    aria-haspopup="menu"
                    aria-expanded={statusMenuOpen}
                    aria-label={t('presence.setStatus')}
                    title={t('presence.setStatus')}
                    onClick={() => setStatusMenuOpen((v) => !v)}
                  >
                    <AvatarStatusBadge userId={user?.id} avatarSize={32} />
                  </button>
                </UserAvatar>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p className="acct-menu__name" title={user?.name}>{user?.name}</p>
                  <p className="acct-menu__email" title={user?.email}>{user?.email}</p>
                </div>
              </div>

              {/* Online / Away / Busy — user-set, this device only. The row and
                  the dot on the avatar above are two triggers for ONE list. */}
              <StatusMenu open={statusMenuOpen} onOpenChange={setStatusMenuOpen} />

              {/* Nav Items */}
              <div className="acct-menu__nav">
                <button
                  type="button"
                  className="acct-menu__item"
                  onClick={() => { setShowUserMenu(false); onOpenSettings?.('account'); }}
                >
                  <div className="acct-menu__icon-box"><User size={13} /></div>
                  <div className="acct-menu__item-text">
                    <span className="acct-menu__item-title">{t('titlebar.profile')}</span>
                    <span className="acct-menu__item-sub">Manage your account</span>
                  </div>
                  <ChevronRight size={12} className="acct-menu__chevron" />
                </button>

                <button
                  type="button"
                  className="acct-menu__item"
                  onClick={() => { setShowUserMenu(false); onOpenSettings?.(); }}
                >
                  <div className="acct-menu__icon-box"><Settings size={13} /></div>
                  <div className="acct-menu__item-text">
                    <span className="acct-menu__item-title">{t('titlebar.settings')}</span>
                    <span className="acct-menu__item-sub">Preferences &amp; shortcuts</span>
                  </div>
                  <ChevronRight size={12} className="acct-menu__chevron" />
                </button>

                <button
                  type="button"
                  className="acct-menu__item"
                  onClick={() => { setShowUserMenu(false); onOpenSettings?.('billing'); }}
                >
                  <div className="acct-menu__icon-box"><CreditCard size={13} /></div>
                  <div className="acct-menu__item-text">
                    <span className="acct-menu__item-title">Plan &amp; billing</span>
                    <span className="acct-menu__item-sub" style={{ textTransform: 'capitalize' }}>{user?.plan ?? 'free'} plan</span>
                  </div>
                  {(() => {
                    const cfg = PLAN_CFG[user?.plan ?? 'free'] || PLAN_CFG.free;
                    return (
                      <span
                        className="acct-menu__plan-badge"
                        style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}
                      >
                        {user?.plan ?? 'free'}
                      </span>
                    );
                  })()}
                </button>
              </div>

              {/* Switch account · Sign out */}
              <div className="acct-menu__footer">
                <button type="button" className="acct-menu__signout" onClick={handleSwitchAccount}>
                  <div className="acct-menu__signout-icon"><UserPlus size={13} /></div>
                  {t('titlebar.switchAccount', 'Switch account')}
                </button>
                <button type="button" className="acct-menu__signout" onClick={handleLogout}>
                  <div className="acct-menu__signout-icon"><LogOut size={13} /></div>
                  {t('titlebar.signOut')}
                </button>
              </div>
            </div>
          )}
        </div>

        {!isMac && isTauri() && (
          <WindowButtons onClose={handleClose} onMinimize={handleMinimize} onMaximize={handleMaximize} />
        )}
      </div>

    </div>

    </>
  );
};

export default TauriTitleBar;
